import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FaTachometerAlt, 
  FaMapMarkerAlt, 
  FaUsers, 
  FaTruck, 
  FaExclamationTriangle, 
  FaMapPin, 
  FaBriefcase,
  FaBuilding,
  FaBars,
  FaTimes,
  FaCog,
  FaSignOutAlt,
  FaUser,
  FaChevronDown
} from 'react-icons/fa';

const Header = ({ activePage, setActivePage }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Color constants
  const PRIMARY_COLOR = '#e28743';
  const SECONDARY_COLOR = '#304a3f';

  useEffect(() => {
    // Get user data from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUserRole(parsedUser.role || 'user');
      } catch (error) {
        setUserRole('user');
      }
    }
  }, []);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        const hamburgerButton = document.querySelector('.hamburger-button');
        if (!hamburgerButton?.contains(event.target)) {
          setMobileMenuOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Menu items configuration
  const menuConfig = {
    base: [
      { name: 'Overview', icon: FaTachometerAlt, path: '/dashboard', roles: ['super_admin', 'admin', 'company_admin', 'user'] },
      { name: 'Fleet Map', icon: FaMapMarkerAlt, path: '/fleet-map', roles: ['super_admin', 'admin', 'company_admin', 'user'] },
      { name: 'Drivers', icon: FaUsers, path: '/drivers', roles: ['super_admin', 'admin', 'company_admin'] },
      { name: 'Vehicles', icon: FaTruck, path: '/vehicles', roles: ['super_admin', 'admin', 'company_admin'] },
      { name: 'Alerts', icon: FaExclamationTriangle, path: '/alerts', roles: ['super_admin', 'admin', 'company_admin', 'user'] },
      { name: 'Talent Hub', icon: FaMapPin, path: '/talent-hub', roles: ['super_admin', 'admin', 'company_admin'] },
    ],
    companyProfile: [
      { name: 'Company Profile', icon: FaBuilding, path: '/company-profile', roles: ['company_admin'] }, // Removed super_admin from here
    ],
    superAdmin: [
      { name: 'Companies', icon: FaBriefcase, path: '/companies', roles: ['super_admin'] },
    ]
  };

  // Get filtered menu items based on role
  const getMenuItems = () => {
    if (!userRole) return menuConfig.base;
    
    const allItems = [...menuConfig.base];
    
    // Company Profile only for company_admin (NOT super_admin)
    if (userRole === 'company_admin') {
      allItems.push(...menuConfig.companyProfile);
    }
    
    // Companies only for super_admin
    if (userRole === 'super_admin') {
      allItems.push(...menuConfig.superAdmin);
    }
    
    return allItems.filter(item => item.roles.includes(userRole));
  };

  const menuItems = getMenuItems();

  // Set active page based on current route
  useEffect(() => {
    const currentMenuItem = menuItems.find(item => item.path === location.pathname);
    if (currentMenuItem) {
      setActivePage(currentMenuItem.name);
    }
  }, [location.pathname, menuItems, setActivePage]);

  const handleNavigation = (item) => {
    setActivePage(item.name);
    navigate(item.path);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login');
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  };

  const handleSettings = () => {
    // Redirect super admin to companies page instead of company profile
    if (userRole === 'super_admin') {
      navigate('/companies');
    } else if (userRole === 'company_admin') {
      navigate('/company-profile');
    } else {
      navigate('/settings');
    }
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  };

  const handleProfile = () => {
    // Redirect super admin to companies page instead of company profile
    if (userRole === 'super_admin') {
      navigate('/companies');
    } else if (userRole === 'company_admin') {
      navigate('/company-profile');
    } else {
      navigate('/profile');
    }
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  };

  const getUserInfo = () => {
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        return {
          name: userData.name || '',
          role: userData.role || 'user',
        };
      } catch (error) {
        return {
          name: '',
          role: 'user',
        };
      }
    }
    return {
      name: '',
      role: 'user',
    };
  };

  const userInfo = getUserInfo();

  return (
    <>
      {/* Main Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          {/* Main Navigation Bar */}
          <div className="flex items-center justify-between h-16">
            {/* Logo Section - Left */}
            <div className="flex items-center">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0 mr-2"
                style={{ backgroundColor: PRIMARY_COLOR }}
              >
                F
              </div>
              <h1 
                className="text-lg font-bold hidden sm:block"
                style={{ color: SECONDARY_COLOR }}
              >
                FleetsCounty
              </h1>
            </div>

            {/* Desktop Navigation - Center */}
            <nav className="hidden lg:flex items-center justify-center flex-1 px-4">
              <div className="flex space-x-1">
                {menuItems.map((item) => {
                  const IconComponent = item.icon;
                  const isActive = activePage === item.name;
                  return (
                    <button
                      key={item.name}
                      onClick={() => handleNavigation(item)}
                      className={`flex items-center px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                        isActive 
                          ? 'shadow-sm' 
                          : 'hover:bg-gray-50'
                      }`}
                      style={{
                        color: isActive ? PRIMARY_COLOR : SECONDARY_COLOR,
                        backgroundColor: isActive ? `${PRIMARY_COLOR}10` : 'transparent',
                      }}
                    >
                      <IconComponent 
                        className="mr-2" 
                        size={14} 
                        style={{ 
                          color: isActive ? PRIMARY_COLOR : SECONDARY_COLOR 
                        }} 
                      />
                      {item.name}
                    </button>
                  );
                })}
              </div>
            </nav>

            {/* User Actions - Right */}
            <div className="flex items-center space-x-3">
              {/* Desktop User Menu */}
              <div className="hidden lg:block relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  style={{ color: SECONDARY_COLOR }}
                >
                  <div 
                    className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-semibold"
                    style={{ backgroundColor: PRIMARY_COLOR }}
                  >
                    {userInfo.name ? userInfo.name.charAt(0).toUpperCase() : 'U'}
                  </div>
                  <FaChevronDown 
                    size={12} 
                    className={`transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* User Dropdown Menu */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-4 py-3">
                      <p className="text-sm font-medium" style={{ color: SECONDARY_COLOR }}>
                        {userInfo.name || 'User'}
                      </p>
                    </div>
                    
                    <button
                      onClick={handleProfile}
                      className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <FaUser className="mr-3" size={14} style={{ color: PRIMARY_COLOR }} />
                      {userRole === 'super_admin' ? 'Companies' : 'Profile'}
                    </button>
                    
                    <button
                      onClick={handleSettings}
                      className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <FaCog className="mr-3" size={14} style={{ color: PRIMARY_COLOR }} />
                      Settings
                    </button>
                    
                    <div className="border-t border-gray-100 my-1"></div>
                    
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2.5 text-sm hover:bg-red-50 transition-colors duration-200 font-medium"
                      style={{ color: PRIMARY_COLOR }}
                    >
                      <FaSignOutAlt className="mr-3" size={14} />
                      Logout
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile Hamburger Menu */}
              <div className="flex lg:hidden items-center">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="hamburger-button p-2 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  style={{ color: SECONDARY_COLOR }}
                  aria-label="Toggle menu"
                >
                  {mobileMenuOpen ? (
                    <FaTimes size={20} style={{ color: PRIMARY_COLOR }} />
                  ) : (
                    <FaBars size={20} />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          <div 
            ref={mobileMenuRef}
            className={`lg:hidden fixed inset-y-0 right-0 w-full max-w-xs bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 ${
              mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {/* Mobile Menu Header */}
            <div className="p-4 border-b" style={{ backgroundColor: SECONDARY_COLOR }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold mr-3"
                    style={{ backgroundColor: PRIMARY_COLOR }}
                  >
                    {userInfo.name ? userInfo.name.charAt(0).toUpperCase() : 'F'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-sm">
                      {userInfo.name || 'FleetsCounty'}
                    </h3>
                  </div>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-white hover:bg-opacity-20"
                >
                  <FaTimes size={18} className="text-white" />
                </button>
              </div>
            </div>

            {/* Mobile Menu Content */}
            <div className="h-[calc(100vh-80px)] overflow-y-auto">
              {/* Navigation Links */}
              <div className="p-2">
                <nav className="space-y-1">
                  {menuItems.map((item) => {
                    const IconComponent = item.icon;
                    const isActive = activePage === item.name;
                    return (
                      <button
                        key={item.name}
                        onClick={() => handleNavigation(item)}
                        className={`flex items-center w-full p-3 rounded-lg transition-colors duration-200 ${
                          isActive ? 'bg-orange-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <IconComponent 
                          className="mr-3" 
                          size={16} 
                          style={{ 
                            color: isActive ? PRIMARY_COLOR : SECONDARY_COLOR 
                          }} 
                        />
                        <span 
                          className="font-medium text-sm"
                          style={{ color: isActive ? PRIMARY_COLOR : SECONDARY_COLOR }}
                        >
                          {item.name}
                        </span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Account Section */}
              <div className="p-2 border-t mt-4">
                <div className="space-y-1">
                  <button
                    onClick={handleProfile}
                    className="flex items-center w-full p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    <FaUser className="mr-3" size={16} style={{ color: PRIMARY_COLOR }} />
                    <span className="text-sm" style={{ color: SECONDARY_COLOR }}>
                      {userRole === 'super_admin' ? 'Companies' : 'Profile'}
                    </span>
                  </button>
                  
                  <button
                    onClick={handleSettings}
                    className="flex items-center w-full p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    <FaCog className="mr-3" size={16} style={{ color: PRIMARY_COLOR }} />
                    <span className="text-sm" style={{ color: SECONDARY_COLOR }}>Settings</span>
                  </button>
                  
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full p-3 rounded-lg hover:bg-red-50 transition-colors duration-200 mt-4"
                  >
                    <FaSignOutAlt className="mr-3" size={16} style={{ color: PRIMARY_COLOR }} />
                    <span className="text-sm font-medium" style={{ color: PRIMARY_COLOR }}>Logout</span>
                  </button>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t mt-4">
                <div className="text-center">
                  <p className="text-xs text-gray-500">FleetsCounty Dashboard</p>
                </div>
              </div>
            </div>
          </div>

          {/* Overlay for mobile menu */}
          {mobileMenuOpen && (
            <div 
              className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setMobileMenuOpen(false)}
            />
          )}
        </div>
      </header>
    </>
  );
};

export default Header;